import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, throwError } from 'rxjs';
import { catchError, first, map, switchMap, take } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';

import {
  GetBusinessAddressesQuery,
  GetPersonalAddressesQuery,
} from '~app/modules/checkout/graphql/get-addresses-query';
import { ApiConfig, CountryCode } from '~shared/constants';
import { ITALY_PROVINCES } from '~shared/constants/italy-provinces.constant';
import { CountriesService } from '~shared/services/countries.service';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { AddressMetroX } from '~app/modules/account/modules/address-book/services/address-metrox.model';
import { IAddressDB } from '~shared/modules/addresses/models/address.model';
import {
  CONTACT_CHANNEL_TYPE_BILLING_ADDRESS,
  CONTACT_CHANNEL_TYPE_SHIPPING_ADDRESS,
} from '~app/modules/account/modules/address-book/constants/address-metrox.constant';
import { Country } from '~shared/models';
import { Address } from '~app/modules/checkout/models';
import { environment } from '~env/environment';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

interface PersonalAddressResponse {
  personData: {
    addresses: AddressMetroX[];
  };
}

interface BusinessAddressResponse {
  accountData: {
    addresses: AddressMetroX[];
  };
}

interface AddressApi {
  addresseeCompany?: string;
  addresseeFirstName: string;
  addresseeLastName: string;
  addressSupplement: string;
  buildingNumber: string;
  channels: ChannelMap | ChannelArray;
  city: string;
  country: string;
  postalCode: string;
  province?: string;
  street: string;
}

type ChannelMap = {
  [CONTACT_CHANNEL_TYPE_SHIPPING_ADDRESS]: boolean;
  [CONTACT_CHANNEL_TYPE_BILLING_ADDRESS]: boolean;
};
type ChannelArray = string[];

/**
 * @see CSS (Customer Self Service) API
 * API Documentation Person Self Service:
 * https://developers.metro.digital/apidetails/O00214/css/person-self-service-frontend-svc
 * API Documentation Business Account Self Service:
 * https://developers.metro.digital/apidetails/O00214/css/accounts-self-service-frontend-svc
 * Personal GraphQL Playground:
 * https://my-pp.makro.es/personal/api/ui/graphql
 * Business GraphQL Playground:
 * https://my-pp.makro.es/business/api/ui/graphql
 */
@Injectable({
  providedIn: 'root',
})
export class AddressMetroXHttpService {
  public countries: Country[];
  private correlationId = 'x-correlation-id';
  public isItalianMarket: boolean;
  private italyProvinces = ITALY_PROVINCES;

  accountInfo$ = this.accountInfoService.getAccountInfo();
  personalContext = 'personal';

  constructor(
    private httpClient: HttpClient,
    private countriesService: CountriesService,
    private accountInfoService: AccountInfoService,
    private serverLogger: ServerLoggerService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {
    this.countriesService.countries$.pipe(first()).subscribe((countries: Country[]) => {
      this.countries = countries;
    });

    this.isItalianMarket = getCountryDomainCode(this.webAppBuyerRequest) === CountryCode.Italy;
  }

  private getRestUrl(businessAccountId): string {
    const countryId = getCountryDomainCode(this.webAppBuyerRequest);

    return businessAccountId !== this.personalContext
      ? `${ApiConfig.SVC_METRONOM_CSS_PROXY_BASE()}/business/api/v1/${countryId}/account/${businessAccountId}`
      : `${ApiConfig.SVC_METRONOM_CSS_PROXY_BASE()}/personal/api/v1/${countryId}`;
  }

  private getGraphQlUrl(businessAccountId): string {
    const countryId = getCountryDomainCode(this.webAppBuyerRequest);

    return businessAccountId !== this.personalContext
      ? `${ApiConfig.SVC_METRONOM_CSS_PROXY_BASE()}/business/api/${countryId}`
      : `${ApiConfig.SVC_METRONOM_CSS_PROXY_BASE()}/personal/api/${countryId}`;
  }

  get(): Observable<IAddressDB[]> {
    return this.accountInfo$.pipe(
      take(1),
      switchMap((account) => {
        const businessAccountId = account?.activeContext;
        const url = `${this.getGraphQlUrl(businessAccountId)}/graphql`;
        return businessAccountId !== this.personalContext
          ? this.getBusiness(url, businessAccountId)
          : this.getPersonal(url);
      })
    );
  }

  getWithBuyerAccountAPIEndpoint(): Observable<IAddressDB[]> {
    const url = `${ApiConfig.SVC_BUYER_ACCOUNT_API_V2_URL()}/accounts/me/address-book`;
    return this.accountInfo$.pipe(
      take(1),
      switchMap((account) => {
        return this.httpClient.get(url, { headers: { 'X-Active-Context': account?.activeContext } }).pipe(
          map((response: any) => {
            const filteredAddressesType = (response?.addresses || []).filter((address) => {
              return !address.contactChannels.length || !!this.isContactChannelTypeBillingOrShipping(address);
            });

            return filteredAddressesType.map((address) => this.getAddressFromMetroXAddress(address));
          }),
          catchError((error: any) => {
            this.serverLogger.error('Checkout - Address book Request - Failed', {
              ...error,
              correlationId: error?.sourceError?.header?.get(this.correlationId),
            });

            return throwError(error);
          })
        );
      })
    );
  }

  getInImpersonationSession() {
    const SVC_BUYER_ACCOUNT_API_V2_URL = `${environment.svcBuyerAccountBaseUrl()}/api/account/v2`;
    const url = `${SVC_BUYER_ACCOUNT_API_V2_URL}/accounts/me/addresses`;
    return this.httpClient.get(url).pipe(
      map((response: any) => {
        return [
          this.getAddressFromMetroXAddress(response.shippingAddress),
          this.getAddressFromMetroXAddress(response.billingAddress),
        ];
      })
    );
  }

  private getPersonal(url: string) {
    return this.httpClient
      .post<{ data: PersonalAddressResponse }>(url, {
        query: GetPersonalAddressesQuery,
      })
      .pipe(
        map((response) => {
          const { addresses } = response.data.personData;
          return addresses.map((address) => this.getAddressFromMetroXAddress(address));
        }),
        catchError((error: any) => {
          this.serverLogger.error('Checkout - Personal Address Request - Failed', {
            ...error,
            correlationId: error?.sourceError?.header?.get(this.correlationId),
          });
          return throwError(error);
        })
      );
  }

  private getBusiness(url: string, businessAccountId) {
    return this.httpClient
      .post<{ data: BusinessAddressResponse }>(url, {
        query: GetBusinessAddressesQuery,
        variables: { accountId: businessAccountId },
      })
      .pipe(
        map((response) => {
          const { addresses } = response.data.accountData;
          const filteredAddressesType = addresses.filter((address) => {
            return !address.contactChannels.length || !!this.isContactChannelTypeBillingOrShipping(address);
          });

          return filteredAddressesType.map((address) => this.getAddressFromMetroXAddress(address));
        }),
        catchError((error: any) => {
          this.serverLogger.error('Checkout - Business Address Request - Failed', {
            ...error,
            correlationId: error?.sourceError?.header?.get(this.correlationId),
          });

          return throwError(error);
        })
      );
  }

  private isContactChannelTypeBillingOrShipping(address: AddressMetroX) {
    return address.contactChannels.find((contactChannel) => {
      return (
        contactChannel.type === CONTACT_CHANNEL_TYPE_SHIPPING_ADDRESS ||
        contactChannel.type === CONTACT_CHANNEL_TYPE_BILLING_ADDRESS
      );
    });
  }

  create(address: IAddressDB): Observable<IAddressDB> {
    return this.accountInfo$.pipe(
      take(1),
      switchMap((account) => {
        const url = `${this.getRestUrl(account?.activeContext)}/address`;
        const body = this.getAddressApiFromAddress(address);
        return this.postAddressToDBToCSS(url, body, address);
      })
    );
  }

  createShippingAndBillingAddressOnTierTwoSignup(address: IAddressDB, accountId: string): Observable<IAddressDB> {
    const countryId = getCountryDomainCode(this.webAppBuyerRequest);
    const url = `${ApiConfig.SVC_METRONOM_CSS_PROXY_BASE()}/business/api/v1/${countryId}/account/${accountId}/address`;
    const body = this.getAddressApiFromAddress(address);

    return this.postAddressToDBToCSS(url, body, address);
  }

  // this function will replace the function above 'createShippingAndBillingAddressOnTierTwoSignup` when we clean up FF
  createShippingAndBillingAddressWithoutCSSApi(address: IAddressDB, accountId: string): Observable<IAddressDB> {
    const url = `${environment.svcBuyerAccountBaseUrl()}/api/v1/business-account/${accountId}/address`;
    const body = this.getAddressApiFromAddress(address);
    const newBodyWithChannels = {
      ...body,
      channels: [CONTACT_CHANNEL_TYPE_SHIPPING_ADDRESS, CONTACT_CHANNEL_TYPE_BILLING_ADDRESS],
    };

    return this.postAddressToDBToCSS(url, newBodyWithChannels, address);
  }

  createShippingAndBillingAddressV2(address: IAddressDB): Observable<IAddressDB> {
    const url = `${environment.svcBuyerAccountBaseUrl()}/api/account/v2/accounts/me/addresses`;
    const body = this.getAddressApiFromAddress(address);
    delete body?.channels;
    const newBodyWithChannels = {
      ...body,
      contactChannels: [
        {
          type: CONTACT_CHANNEL_TYPE_SHIPPING_ADDRESS,
        },
        {
          type: CONTACT_CHANNEL_TYPE_BILLING_ADDRESS,
        },
      ],
    };

    return this.postAddressV2(url, newBodyWithChannels, address);
  }

  postAddressV2(url: string, body: AddressApi, address) {
    return this.accountInfo$.pipe(
      take(1),
      switchMap((account) => {
        return this.httpClient.post(url, body, { headers: { 'X-Active-Context': account.activeContext } }).pipe(
          map((response: { id: string }) => {
            return { ...address, id: response.id };
          }),
          catchError((error: any) => {
            this.serverLogger.error('Registration - Create Address Request - Failed', {
              ...error,
              correlationId: error?.sourceError?.header?.get(this.correlationId),
            });

            return throwError(error);
          })
        );
      })
    );
  }

  postAddressToDBToCSS(url: string, body: AddressApi, address: IAddressDB) {
    return this.httpClient.post(url, body).pipe(
      map((response: { id: string }) => {
        return { ...address, id: response.id };
      }),
      catchError((error: any) => {
        this.serverLogger.error('Checkout - Create Address Request - Failed', {
          ...error,
          correlationId: error?.sourceError?.header?.get(this.correlationId),
        });

        return throwError(error);
      })
    );
  }

  update(address: IAddressDB): Observable<IAddressDB> {
    const body = this.getAddressApiFromAddress(address);

    return this.accountInfo$.pipe(
      take(1),
      map((account) => {
        return account?.activeContext;
      }),
      switchMap((businessAccountId) =>
        this.httpClient.put(`${this.getRestUrl(businessAccountId)}/address/${address.id}`, body).pipe(
          map(() => {
            return { ...address } as IAddressDB;
          }),
          catchError((error: any) => {
            this.serverLogger.error('Checkout - Update Address Request - Failed', {
              ...error,
              correlationId: error?.sourceError?.header?.get(this.correlationId),
            });

            return throwError(error);
          })
        )
      )
    );
  }

  createBatch(addresses: Address[]): Observable<Address[]> {
    return forkJoin([this.create(addresses[0]), this.create(addresses[1])]);
  }

  private getAddressApiFromAddress(address: IAddressDB): AddressApi {
    const {
      country,
      postalCode,
      city,
      street,
      houseNumber,
      firstName,
      lastName,
      defaultBilling,
      defaultShipping,
      companyName,
      addressLine2,
      stateOrProvince,
    } = address;
    const countryCode = this.countries.find((countryItem) => countryItem.id === country).code;

    return {
      addresseeCompany: companyName,
      addresseeFirstName: firstName,
      addresseeLastName: lastName,
      buildingNumber: houseNumber,
      addressSupplement: addressLine2,
      city,
      channels: {
        [CONTACT_CHANNEL_TYPE_SHIPPING_ADDRESS]: defaultShipping || false,
        [CONTACT_CHANNEL_TYPE_BILLING_ADDRESS]: defaultBilling || false,
      },
      country: countryCode,
      postalCode,
      street,
      ...(this.isItalianMarket ? { province: this.getProvinceCode(stateOrProvince) } : null),
    };
  }

  private getAddressFromMetroXAddress(address: AddressMetroX): IAddressDB {
    const {
      id,
      country,
      postalCode,
      city,
      street,
      buildingNumber,
      addressSupplement,
      addresseeFirstName,
      addresseeLastName,
      contactChannels,
      addresseeCompany,
      province,
    } = address;
    const countryId = country;
    const countryWithIso = this.countries.find((countryItem) => countryItem.code === countryId);

    return {
      id,
      country: countryWithIso.id,
      postalCode,
      city,
      street,
      houseNumber: buildingNumber,
      addressLine2: addressSupplement,
      firstName: addresseeFirstName,
      lastName: addresseeLastName,
      ...(this.isItalianMarket ? { stateOrProvince: this.getProvinceName(province) } : null),
      defaultShipping: !!contactChannels.find(
        (contactChannel) => contactChannel.type === CONTACT_CHANNEL_TYPE_SHIPPING_ADDRESS
      ),
      defaultBilling: !!contactChannels.find(
        (contactChannel) => contactChannel.type === CONTACT_CHANNEL_TYPE_BILLING_ADDRESS
      ),
      companyName: addresseeCompany,
    };
  }

  private getProvinceCode(provinceName: string): string {
    const provinceCode = this.italyProvinces.find((italyProvince) => italyProvince.name === provinceName)?.code;
    return provinceCode ? provinceCode : provinceName;
  }

  private getProvinceName(provinceCode: string): string {
    const provinceName = this.italyProvinces.find((italyProvince) => italyProvince.code === provinceCode)?.name;
    return provinceName ? provinceName : provinceCode;
  }
}
